import { Observable, catchError, map, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Port } from '@navstation-portal/ports/data-model/ports';
import { portCodeToSelectOption } from '@navstation-portal/ports/util/ports';
import {
  ApiReportResponse,
  IOffshoreReport,
  IOffshoreReportWithId,
  OffshoreFuelSpecState,
} from '@navstation-portal/reporting/data-model/offshore-report';
import { BunkerRowEntry } from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { ApiErrorMapper, BASE_API_V2_URL } from '@navstation-portal/shared/util/api';
import { resolveTimezoneSelectOption } from '@navstation-portal/shared/util/time';
import { EntityId } from '@ngrx/signals/entities';

const REPORT_ROUTE = 'api/reports';

function directionToSelectOption(direction: string | undefined): SelectOption | undefined {
  if (!direction) return undefined;
  return {
    code: direction.toUpperCase(),
    name: direction.toUpperCase(),
  };
}

@Injectable({
  providedIn: 'root',
})
export class OffshoreReportService {
  private http = inject(HttpClient);
  private readonly baseUrl = inject(BASE_API_V2_URL);
  private apiError = inject(ApiErrorMapper).forFeature('OffshoreReport');

  fetchReport(vesselId: string, reportId: EntityId, ports: Port[]): Observable<IOffshoreReportWithId> {
    return this.http.get<ApiReportResponse>(`${this.baseUrl}/${REPORT_ROUTE}/${reportId}`).pipe(
      map((response): IOffshoreReportWithId => {
        const report = response.fields ?? {};

        return {
          reportId: response.report_id,
          report: {
            ...report,
            operational: {
              ...report.operational,
              reportOptions: {
                ...report.operational?.reportOptions,
                eventType: response.event,
                operationType: response.operation,
              },
              reportDetails: {
                ...report.operational?.reportDetails,
                reportFrom: response.period_from ? new Date(response.period_from) : undefined,
                reportTo: response.period_to ? new Date(response.period_to) : undefined,
                reportCreation: response.created ? new Date(response.created) : undefined,
                reportSubmitted: response.updated ? new Date(response.updated) : undefined,
                timezone: resolveTimezoneSelectOption(response.timezone),
                previousTimezone: resolveTimezoneSelectOption(report.operational?.reportDetails?.previousTimezone),
              },
              voyageDetails: {
                ...report.operational?.voyageDetails,
                voyageFrom: portCodeToSelectOption(
                  report.operational?.voyageDetails?.voyageFrom as unknown as string,
                  ports
                ),
                voyageTo: portCodeToSelectOption(
                  report.operational?.voyageDetails?.voyageTo as unknown as string,
                  ports
                ),
                etaTimezone: resolveTimezoneSelectOption(report.operational?.voyageDetails?.etaTimezone),
              },
            },
            positionAndLogistics: {
              ...report.positionAndLogistics,
              shipPosition: {
                ...report.positionAndLogistics?.shipPosition,
                position: {
                  ...report.positionAndLogistics?.shipPosition?.position,
                  latDirection: directionToSelectOption(
                    report.positionAndLogistics?.shipPosition?.position?.latDirection as unknown as string
                  ),
                  longDirection: directionToSelectOption(
                    report.positionAndLogistics?.shipPosition?.position?.longDirection as unknown as string
                  ),
                },
              },
            },
          },
        };
      }),
      catchError(this.apiError.toApiError('fetching the offshore report'))
    );
  }

  createReport(reportNumber: number): Observable<IOffshoreReportWithId> {
    const now = new Date().toISOString();
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    const body = {
      period_from: now,
      period_to: tomorrow,
      fields: {
        operational: {
          reportDetails: {
            reportNumber,
            reportCreation: now,
          },
        },
      },
    };
    return this.http.post<ApiReportResponse>(`${this.baseUrl}/${REPORT_ROUTE}`, body).pipe(
      map((response): IOffshoreReportWithId => {
        const createdReport = response.fields ?? {};

        return {
          reportId: response.report_id,
          report: {
            ...createdReport,
            operational: {
              ...createdReport.operational,
              reportOptions: {
                ...createdReport.operational?.reportOptions,
                eventType: response.event,
                operationType: response.operation,
              },
              reportDetails: {
                ...createdReport.operational?.reportDetails,
                reportFrom: response.period_from ? new Date(response.period_from) : undefined,
                reportTo: response.period_to ? new Date(response.period_to) : undefined,
                reportCreation: response.created ? new Date(response.created) : undefined,
                reportSubmitted: response.updated ? new Date(response.updated) : undefined,
                timezone: response.timezone
                  ? {
                      name: response.timezone,
                      code: response.timezone,
                    }
                  : createdReport.operational?.reportDetails?.timezone,
              },
            },
          },
        };
      }),
      catchError(this.apiError.toApiError('creating the offshore report'))
    );
  }

  saveReport(reportId: EntityId, report: Partial<IOffshoreReport>): Observable<IOffshoreReportWithId> {
    const body = {
      event: report.operational?.reportOptions?.eventType,
      operation: report.operational?.reportOptions?.operationType,
      status: 'draft',
      period_from: report.operational?.reportDetails?.reportFrom,
      period_to: report.operational?.reportDetails?.reportTo,
      timezone: report.operational?.reportDetails?.timezone,
      fields: report,
    };

    const originalEventType = report.operational?.reportOptions?.eventType;
    const originalOperationType = report.operational?.reportOptions?.operationType;
    const originalReportFrom = report.operational?.reportDetails?.reportFrom;
    const originalReportTo = report.operational?.reportDetails?.reportTo;
    const originalTimezone = report.operational?.reportDetails?.timezone;
    delete report.operational?.reportOptions?.eventType;
    delete report.operational?.reportOptions?.operationType;
    delete report.operational?.reportDetails?.reportFrom;
    delete report.operational?.reportDetails?.reportTo;
    delete report.operational?.reportDetails?.timezone;
    report.consumptions?.fuelSpecs?.forEach((spec: OffshoreFuelSpecState) => delete spec.rowId);
    report.bunker?.fuelOilBunkerTable?.bunkerRows?.forEach((row: BunkerRowEntry) => delete row.rowId);

    return this.http.put<ApiReportResponse>(`${this.baseUrl}/${REPORT_ROUTE}/${reportId}`, body).pipe(
      map((): IOffshoreReportWithId => {
        if (originalEventType && report?.operational?.reportOptions)
          report.operational.reportOptions.eventType = originalEventType;
        if (originalEventType && report?.operational?.reportOptions)
          report.operational.reportOptions.operationType = originalOperationType;
        if (originalReportFrom && report?.operational?.reportDetails)
          report.operational.reportDetails.reportFrom = originalReportFrom;
        if (originalReportTo && report?.operational?.reportDetails)
          report.operational.reportDetails.reportTo = originalReportTo;
        if (originalReportTo && report?.operational?.reportDetails)
          report.operational.reportDetails.timezone = originalTimezone;

        return {
          reportId,
          report,
        };
      }),
      catchError(this.apiError.toApiError('saving the offshore report'))
    );
  }

  deleteReport(reportId: EntityId): Observable<EntityId> {
    return this.http.delete<ApiReportResponse>(`${this.baseUrl}/${REPORT_ROUTE}/${reportId}`).pipe(
      map((): EntityId => reportId),
      catchError(this.apiError.toApiError('deleting the offshore report'))
    );
  }

  submitReport(reportId: EntityId, report: Partial<IOffshoreReport>): Observable<IOffshoreReportWithId> {
    void reportId;
    void report;
    return of({} as IOffshoreReportWithId);
  }
}
